import { Api } from '../api';
import { capitalize } from '../util';

export type Approval = 'approved' | 'rejected';

/**
 * The fields every view of a skill carries, so these functions also work on
 * the lighter skills embedded in ticket lists.
 */
type Skill = Pick<
  Api.TicketSkill.V,
  'type' | 'approved' | 'assignee' | 'status'
>;

// Mirrors TicketSkillType::requiresApproval() on the backend.
const TYPES_NEEDING_APPROVAL = ['design', 'backend', 'frontend'];

export function requiresApproval(skill: Skill): boolean {
  return TYPES_NEEDING_APPROVAL.includes(skill.type);
}

/**
 * Null until someone has reviewed the skill.
 */
export function getApproval(skill: Skill): Approval | null {
  if (skill.approved === null) return null;
  return skill.approved ? 'approved' : 'rejected';
}

export function getApprovedValue(approval: Approval | null): boolean | null {
  return approval === null ? null : approval === 'approved';
}

/**
 * Whether the API will accept an assignee on the skill yet. Rejecting a skill
 * also clears its assignee, server side.
 */
export function isAssignable(skill: Skill): boolean {
  return !requiresApproval(skill) || skill.approved === true;
}

export function getTypeName(type: string): string {
  return type === 'qa' ? 'QA' : capitalize(type);
}

/**
 * Skills the ticket still needs somebody to pick up.
 */
export function filterUnassigned<S extends Skill>(
  skills: ReadonlyArray<S>
): Array<S> {
  return skills.filter((skill) => !skill.assignee && isAssignable(skill));
}

/**
 * Of the skills nobody has picked up, those the user is able to do.
 */
export function filterTakeableBy<S extends Skill>(
  skills: ReadonlyArray<S>,
  user: Api.User.BaseView
): Array<S> {
  const userSkills = user.skills ?? [];
  return filterUnassigned(skills).filter((skill) =>
    userSkills.includes(skill.type)
  );
}

/**
 * The skills that explain why a ticket is on someone's day: the ones they
 * hold, or the finished ones once they have come off it.
 */
export function filterForUser<S extends Skill>(
  skills: ReadonlyArray<S>,
  userId: string
): Array<S> {
  const held = skills.filter((skill) => skill.assignee?.id === userId);
  if (held.length > 0) return held;
  return skills.filter((skill) => skill.status === 'done');
}
